import type { BeforeSendFn, PostHog } from 'posthog-js';

import { isDaylight, isSpark, Product } from './productHelpers';

import { logError } from '@/utils/logError';
import { envSwitch } from '@/utils/environmentUtils';

type GetDistinctIdFn = (opts: { variables: { email: string } }) => Promise<{
  data?: { posthogDistinctIdWithEmail?: { distinctId?: string | null } | null } | null;
}>;

export function linkPostHogIdentity(
  getDistinctId: GetDistinctIdFn,
  posthog: PostHog | undefined,
  email: string,
  logPrefix = '[PostHog]',
): Promise<void> {
  return getDistinctId({ variables: { email } })
    .then((result) => {
      const distinctId = result?.data?.posthogDistinctIdWithEmail?.distinctId;
      if (distinctId) { posthog?.identify(distinctId, { email }); }
    })
    .catch((e) => { logError(logPrefix, 'PostHog identify failed', e); });
}

export enum PosthogProgram {
  SLEEPIO = 'sleepio',
  SPARK = 'spark_direct',
  DAYLIGHT = 'daylight',
  DAYLIGHT_PLATGEN = 'daylight_platgen',
}

export const SLEEPIO_PROGRAM = PosthogProgram.SLEEPIO;

export const SPARK_PROGRAM = PosthogProgram.SPARK;

export const DAYLIGHT_PROGRAM = PosthogProgram.DAYLIGHT;

// Sleepio Canopy's own product identity for analytics — distinct from SLEEPIO_PROGRAM,
// which getProgram() also returns for canopy (it normalizes to the generic Sleepio bucket).
export const SLEEPIO_CANOPY_PROGRAM = 'sleepio-canopy';

export const postHogHost = 'https://us.i.posthog.com';

// All non-production US environments (dev, stage, pr, qa) use the same PostHog project
export const projectApiKey = envSwitch({
  local: '',
  dev: 'phc_8oCK6vDiV0qmfDsNW7YUQK9t3Vios1oEoz3gPPL893S', // US Non-prod
  stage: 'phc_8oCK6vDiV0qmfDsNW7YUQK9t3Vios1oEoz3gPPL893S', // US Non-prod
  pr: 'phc_8oCK6vDiV0qmfDsNW7YUQK9t3Vios1oEoz3gPPL893S', // US Non-prod
  qa: 'phc_8oCK6vDiV0qmfDsNW7YUQK9t3Vios1oEoz3gPPL893S', // US Non-prod
  prod: 'phc_8QW21BDgUVTJrtxo6cuV7vu8vMqp4020M9jIGoHCHWE', // Production
});

export const getProgram = (product: Product): PosthogProgram => {
  if (isSpark(product)) {
    return SPARK_PROGRAM;
  }
  if (isDaylight(product)) {
    return DAYLIGHT_PROGRAM;
  }
  return SLEEPIO_PROGRAM;
};

export const getPlatgenProgram = (product: Product): PosthogProgram => {
  if (isDaylight(product)) {
    return PosthogProgram.DAYLIGHT_PLATGEN;
  }
  return getProgram(product);
};

// Credentials and personal data that ride in a URL and must never reach a telemetry provider.
//
// `exchange_token` is the one-time credential the app-handoff URL carries (ActivationComplete).
// No surface currently puts that URL into a PostHog event, a Sentry breadcrumb, or a Datadog
// view name — but this list exists precisely for app-handoff URLs, and the cost of listing it
// before that changes is one word.
//
// `access_token` and friends are Supabase's, not ours, and they are the reason this list grew.
// A magic-link sign-in ends on our callback with the whole session in the URL **fragment** —
// `#access_token=<JWT>&expires_at=…&token_type=bearer`. The JWT is a live bearer credential for
// an hour, and its payload carries the member's email address, their auth user id, our own
// person id, and their product entitlements. `token` does NOT cover it: `URLSearchParams.delete`
// matches a whole key, so deleting `token` leaves `access_token` untouched. That gap put a valid
// session token and a member's email into Datadog RUM's stored `url_hash`, found while tracing a
// login defect (PAT-1286).
//
// `provider_token` / `provider_refresh_token` / `id_token` are the same fragment's OAuth
// siblings. Listed now rather than after the next incident, for the same one-word reason.
//
// Deliberately NOT here: `redirectTo` and `hn`. Neither is a credential — the first is an
// address and the second is a per-flow nonce that is useless without the matching install —
// and every caller below is a telemetry hook, so stripping them would only blind us to the
// exact hand-off we most need to debug.
const SENSITIVE_URL_PARAMS = [
  'token',
  'access_token',
  'refresh_token',
  'provider_token',
  'provider_refresh_token',
  'id_token',
  'user_email',
  'exchange_token',
];
const URL_PROPERTIES_TO_SCRUB = ['$current_url', '$session_entry_url', '$referrer'];

// posthog copies the same URLs into PERSON properties, in these two bags, where the loop over
// URL_PROPERTIES_TO_SCRUB never reaches them — it walks fixed top-level keys only.
// `$set_once.$initial_current_url` is built from `location.href` with the fragment intact, and on
// the sign-in path the first page load in that storage context is the magic-link callback carrying
// `#access_token=…&refresh_token=…`. `$set_once` writes a PERMANENT person property, so without
// this the credential outlives the event it arrived on and sits on the person record.
//
// Every string in the bag is scrubbed rather than a named list of properties, because posthog
// composes these names at runtime (`"$initial_" + prop`) — a list here would silently miss whichever
// one it adds next, which is exactly how `$initial_current_url` was missed in the first place.
// stripSensitiveUrlParams is a no-op for any string that is not a URL carrying a listed param.
const PERSON_PROPERTY_BAGS = ['$set', '$set_once'];

// These leak via the app-handoff bounce (see CreatePassword.tsx) into whatever URL field calls this.
// base is only for a relative url (e.g. Sentry's breadcrumb from/to) - the return stays relative too.
export const stripSensitiveUrlParams = (url: string, base?: string): string => {
  try {
    const parsed = new URL(url, base);
    SENSITIVE_URL_PARAMS.forEach((param) => parsed.searchParams.delete(param));
    // The app-handoff bounce now carries these in the fragment instead of the query string.
    const hashParams = new URLSearchParams(parsed.hash.slice(1));
    // Only rewrite the fragment when there's something to strip - a plain anchor would gain a trailing '='.
    if (SENSITIVE_URL_PARAMS.some((param) => hashParams.has(param))) {
      SENSITIVE_URL_PARAMS.forEach((param) => hashParams.delete(param));
      parsed.hash = hashParams.toString();
    }
    return base ? `${parsed.pathname}${parsed.search}${parsed.hash}` : parsed.toString();
  } catch {
    return url;
  }
};

export const beforeSend: BeforeSendFn = (captureResult) => {
  if (!captureResult) {
    return captureResult;
  }
  const properties = { ...captureResult.properties };
  URL_PROPERTIES_TO_SCRUB.forEach((key) => {
    if (typeof properties[key] === 'string') {
      properties[key] = stripSensitiveUrlParams(properties[key] as string);
    }
  });
  PERSON_PROPERTY_BAGS.forEach((bag) => {
    const value = properties[bag];
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return;
    }
    // Copied, not mutated: `properties` is a shallow clone, so writing through would edit the
    // caller's object.
    const scrubbed: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    Object.keys(scrubbed).forEach((key) => {
      if (typeof scrubbed[key] === 'string') {
        scrubbed[key] = stripSensitiveUrlParams(scrubbed[key] as string);
      }
    });
    properties[bag] = scrubbed;
  });
  return { ...captureResult, properties };
};
